import React from 'react';

import { ActionMenu } from '../ActionMenu';
import { Box } from '../Box';
import { IconLabel } from '../IconLabel';
import { Text } from '../Typography';
import PackageCardContentHeader from './components/PackageCardContentHeader';
import PackageCardFeatureChecks from './components/PackageCardFeatureChecks';
import PackageCardMedia from './components/PackageCardMedia';
import PackageCardSkeleton from './components/PackageCardSkeleton';
import {
  actionMenuStyles,
  cardContainerStyles,
  contentContainerStyles,
} from './PackageCard.styles';
import type { PackageCardProps } from './PackageCard.types';

const PackageCard = ({
  images,
  imageComponent,
  imageSizes,
  title,
  subtitle,
  startingPrice,
  supportingText,
  isFavorited = false,
  onFavoriteClick,
  onClick,
  className,
  featureChecks,
  badges,
  availabilityBadges,
  id,
  hasContentBackground = false,
  background = 'white',
  orientation = 'vertical',
  actions,
  size = 'md',
  isLoading = false,
  ...rest
}: PackageCardProps) => {
  if (isLoading) {
    return (
      <PackageCardSkeleton
        className={className}
        hasContentBackground={hasContentBackground}
        orientation={orientation}
      />
    );
  }

  const carouselId = id || `package-card-${title.replace(/\s+/g, '-')}`;

  return (
    <Box
      position="relative"
      css={cardContainerStyles(
        orientation,
        hasContentBackground,
        !!onClick,
        background
      )}
      className={className}
      onClick={onClick}
      {...rest}
    >
      <PackageCardMedia
        actions={actions}
        availabilityBadges={availabilityBadges}
        carouselId={carouselId}
        images={images}
        imageComponent={imageComponent}
        imageSizes={imageSizes}
        carouselLabel={`${title} image gallery`}
        isFavorited={isFavorited}
        onFavoriteClick={onFavoriteClick}
        orientation={orientation}
      />

      {actions && actions.length > 0 && orientation === 'horizontal' && (
        <Box css={actionMenuStyles}>
          <ActionMenu actions={actions} />
        </Box>
      )}

      <Box css={contentContainerStyles(orientation, hasContentBackground)}>
        <PackageCardContentHeader
          size={size}
          startingPrice={startingPrice}
          subtitle={subtitle}
          title={title}
        />

        {featureChecks && (
          <PackageCardFeatureChecks featureChecks={featureChecks} />
        )}

        {badges && badges.length > 0 && size !== 'xs' && (
          <Box
            display="flex"
            gap="var(--spacing-2) var(--spacing-4)"
            alignItems="center"
            flexWrap="wrap"
          >
            {badges.map((badge, index) => (
              <IconLabel
                iconSize={badge.iconSize || 'medium'}
                key={`badge-${badge.variant}-${index}`}
                label={badge.label}
                variant={badge.variant}
              />
            ))}
          </Box>
        )}

        {supportingText && (
          <Box mt="var(--spacing-2)">
            <Text size="xs" fontWeight="bold" color="var(--color-accent-600)">
              {supportingText}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PackageCard;
